import type { AppData, AppSettings, SyncMeta } from './types'

/**
 * Sync envelope for a freshly created local row.
 *
 * `dirty: true` is the whole point: a row written without it is silently never
 * pushed, which is the most common way to lose data here. Every mutation goes
 * through this or `touch()` in `store.ts`.
 */
export function newMeta(): SyncMeta {
  return { updatedAt: new Date().toISOString(), deletedAt: null, dirty: true }
}

/**
 * The epoch, which loses every comparison it takes part in.
 *
 * Settings are the one row every install has before it has anything to say, so
 * they start *clean* and infinitely old rather than new and dirty. A second
 * device that has never opened *Réglages* then adopts the household's hour on
 * its first pull instead of pushing 19:00 over it — the same hazard
 * `../semainier` solves for its demo content with `seedMeta`, in the only place
 * this app has content it did not ask for.
 */
const NEVER_EDITED = '1970-01-01T00:00:00.000Z'

export function defaultSettings(): AppSettings {
  return {
    reminderEnabled: false,
    reminderTime: '19:00',
    // The reminder is compared in the household's own timezone, never the
    // server's clock and never a hardcoded Europe/Brussels — see CLAUDE.md §5.
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Brussels',
    updatedAt: NEVER_EDITED,
    deletedAt: null,
    dirty: false,
  }
}

/**
 * What a brand-new install starts with: nothing.
 *
 * `../semainier` seeds a demo lesson and a sticky note, and needs `seedMeta`,
 * `isSeedRow` and a whole adoption dance to stop those polluting a real
 * account. Here the built-in rotation ships with the bundle rather than being
 * seeded into the store, so an empty `tasks` really is empty — there is nothing
 * a fresh install could push over a household's own chores, and none of that
 * machinery is ported.
 *
 * `members` is empty for the same reason and it is worth saying out loud,
 * because the temptation here is real: a synthesised "Moi" row would give the
 * member picker something to show before the first pull, and it would also be a
 * row marked dirty, which the next sync would push into a household that
 * already has its own people. The picker synthesises its fallback in the view
 * layer instead (`src/App.tsx`), where nothing can ever push it.
 */
export function createDefaults(): AppData {
  return {
    completions: [],
    tasks: [],
    members: [],
    settings: defaultSettings(),
  }
}
